/**
 * InlineDropdown — styled button + absolute listbox.
 *
 * Zero-extra-dependency dropdown that renders inline and closes on click-outside.
 * Replaces the ~70-line inline copy in RatingPageNew.
 */
import React from 'react';
import { ChevronDown } from 'lucide-react';

export function InlineDropdown({
  value,
  options,
  onChange,
  placeholder,
  className = '',
  buttonClassName = '',
}: {
  value: string;
  options: { value: string; label: string; icon?: React.ReactNode }[];
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
  buttonClassName?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  const base =
    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border transition-colors';

  const stateClass = open
    ? 'bg-disney-blue-500 text-white border-disney-blue-500'
    : value
      ? 'bg-white dark:bg-surface-dark-card text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400'
      : 'bg-white dark:bg-surface-dark-card text-gray-400 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400';

  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${base} ${stateClass} ${buttonClassName}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          {selected ? <>{selected.icon}{selected.label}</> : placeholder}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-white dark:bg-surface-dark-card border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1 min-w-[180px] max-h-60 overflow-y-auto"
          role="listbox"
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors ${opt.value === value
                ? 'bg-disney-blue-500 text-white'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              role="option"
              aria-selected={opt.value === value}
            >
              <span className="inline-flex items-center gap-1.5">{opt.icon}{opt.label}</span>
              {opt.value === value && (
                <svg className="w-4 h-4 ml-auto" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
